import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi, { TrashPost } from 'api/post';

const PER_PAGE = 15;

type UseTrashPostsParams = {
  profileIds?: number[];
  enabled?: boolean;
};

export const useTrashPosts = ({
  profileIds,
  enabled = true,
}: UseTrashPostsParams = {}) => {
  const trashedPostsQuery = useInfiniteQuery(
    queryKey.trashedPosts({ profileIds }),
    async ({ pageParam = 1 }) => {
      return PostApi.getTrashPosts({
        profileIds,
        page: pageParam,
        perPage: PER_PAGE,
      });
    },
    {
      enabled,
      getNextPageParam: (lastPage) => {
        const { current_page, last_page } = lastPage.meta;
        return current_page < last_page ? current_page + 1 : undefined;
      },
    },
  );

  const posts = useMemo<TrashPost[]>(() => {
    return trashedPostsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  }, [trashedPostsQuery.data]);

  const totalCount = trashedPostsQuery.data?.pages[0]?.meta.total ?? 0;

  return {
    posts,
    totalCount,
    isLoading: trashedPostsQuery.isLoading,
    isFetchingNextPage: trashedPostsQuery.isFetchingNextPage,
    isError: trashedPostsQuery.isError,
    error: trashedPostsQuery.error,
    hasNextPage: trashedPostsQuery.hasNextPage,
    fetchNextPage: trashedPostsQuery.fetchNextPage,
    refetch: trashedPostsQuery.refetch,
  };
};

type UseTrashPostsCountParams = {
  profileIds?: number[];
  enabled?: boolean;
};

export const useTrashPostsCount = ({
  profileIds,
  enabled = true,
}: UseTrashPostsCountParams = {}) => {
  const countQuery = useQuery(
    queryKey.trashedPostsCount({ profileIds }),
    () => PostApi.getTrashPostsCount({ profileIds }),
    { enabled },
  );

  return {
    count: countQuery.data?.count ?? 0,
    isLoading: countQuery.isLoading,
    refetch: countQuery.refetch,
  };
};
